import { Component, OnInit, Inject, Renderer2, OnDestroy, inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, take, takeUntil, filter } from 'rxjs';
import { INITIAL_USER } from 'src/app/core/constants/app.constants';
import { IUser } from 'src/app/shared/interfaces/interface';
import { AuthService } from 'src/app/services/auth.service';
import { UserAuthService } from 'src/app/services/user-auth.service';
import { ICON_NAMES } from '@modules/cat/constants';
import { NRIconComponent } from '@shared/components/nr-icon/nr-icon.component';
import { RegionRouteService } from '@services/shared/nr-region-route.service';
import { INRSelectOption } from '@shared/interfaces/nr-select-option.interface';
import { TooltipModule } from 'primeng/tooltip';
import { SubHeaderPopoverService } from '@services/shared/sub-header-popover.service';
import { CatCounterService } from '@services/cat-counter.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [FormsModule, CommonModule, NRIconComponent, TooltipModule],
})
export class HeaderComponent implements OnInit, OnDestroy {
  showMenu = false;
  user: IUser = INITIAL_USER;
  selectedRegion!: INRSelectOption;
  selectedRoute!: INRSelectOption;
  catInAppCount = 0;
  showTooltip = false;
  hasCATNotificationStarted = false;
  isTooltipVisible = false;
  shouldPulse = false;
  pulseTimeout: ReturnType<typeof setTimeout> = setTimeout(() => 0);
  lastRegionRouteKey = '';
  icons = ICON_NAMES;
  private previousCatCount: number | null = null;
  private readonly destroy$ = new Subject();
  private readonly regionRouteService = inject(RegionRouteService);
  menuItems: HTMLElement[] = [];


  constructor(
    private readonly authSvc: AuthService,
    private readonly userSvc: UserAuthService,
    private readonly route: Router,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly renderer: Renderer2,
    private readonly subHeaderPopoverService: SubHeaderPopoverService,
    private readonly catCounterService: CatCounterService,
  ) {}

  ngOnInit(): void {
    // Get user info and initialize counter on first load only
    this.userSvc
      .getUser()
      .pipe(
        filter((u) => !!u && !!u.routeId && u.routeId !== 0 && u.firstLogin === false),
        take(1),
      )
      .subscribe((user) => {
        this.user = user;
        // Initialize counter once a valid user profile is available
        this.getCATCounter();
      });

    // Tooltip click listener
    this.renderer.listen('document', 'click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const clickedInsideTooltip = target.closest('.cat-in-app-tooltip');
      const clickedIcon = target.closest('.icon-wrapper');

      if (!clickedInsideTooltip && !clickedIcon) {
        this.showTooltip = false;
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.unsubscribe();
  }

  myProfile() {
    this.route.navigateByUrl('myProfile');
    this.showMenu = false;
  }
  logout() {
    this.authSvc.logout();
  }

  get tooltipText(): string {
    return `${this.catInAppCount} ORSs in an Alert Cycle with no active Speed Restrictions`;
  }

  handleCATBadgeClick(): void {
    this.showTooltip = !this.showTooltip;

    // Stop the pulse animation
    this.shouldPulse = false;

    // Clear the timeout if it's still running
    if (this.pulseTimeout) {
      clearTimeout(this.pulseTimeout);
    }
  }

  navigateFromInAppTooltip(): void {
    this.showTooltip = false;
    this.route.navigate(['cat/summary']);
  }

  getCATCounter(): void {
    this.catCounterService.getCatCount$().pipe(takeUntil(this.destroy$)).subscribe((count) => {
      const newCount = typeof count === 'number' ? count : 0;

      // Only trigger pulse if value changed
      const hasChanged = this.previousCatCount !== null && this.previousCatCount !== newCount;
      this.catInAppCount = newCount;
      this.previousCatCount = newCount;

      if (this.catInAppCount > 0 && hasChanged) {
        this.shouldPulse = true;
        this.subHeaderPopoverService.setcatInAppCount(this.catInAppCount);

        if (this.pulseTimeout) {
          clearTimeout(this.pulseTimeout);
        }

        this.pulseTimeout = setTimeout(() => {
          this.shouldPulse = false;
        }, 7000);
      }
    });
  }

  handleKeyNavigation(event: KeyboardEvent): void {
    if (!this.menuItems.length) return;
  
    const activeIndex = this.menuItems.indexOf(document.activeElement as HTMLElement);
  
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        const next = (activeIndex + 1) % this.menuItems.length;
        this.menuItems[next].focus();
        break;
  
      case 'ArrowUp':
        event.preventDefault();
        const prev = (activeIndex - 1 + this.menuItems.length) % this.menuItems.length;
        this.menuItems[prev].focus();
        break;
  
      case 'Escape':
        this.showMenu = false;
        break;
    }
  }
  
  handleToggleKey(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.showMenu = true;
      this.focusFirstMenuItem();

      setTimeout(() => {
        this.menuItems = Array.from(
          document.querySelectorAll('.profile-dropdown [role="menuitem"]')
        ) as HTMLElement[];
  
        this.menuItems[0]?.focus();
      });
    }
  
    if (event.key === 'Escape') {
      this.showMenu = false;
    }
  }
  

  toggleMenu(): void {
    this.showMenu = !this.showMenu;
  }
  onTouchToggle(event: TouchEvent){
    this.toggleMenu();
  }

  focusFirstMenuItem(): void {
    setTimeout(() => {
      this.menuItems = Array.from(
        document.querySelectorAll('.profile-dropdown [role="menuitem"]')
      ) as HTMLElement[];

      this.menuItems[0]?.focus();
    });
  }

  skipToMainContent(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const main = this.document.getElementById('main-content');
    if (!main) return;

    // Prefer focusing inside primary viewport region if present
    const viewport = main.querySelector('.nr-viewport') as HTMLElement | null;
    const scope = viewport ?? main;

    const preferred = this.getPreferredSkipTarget(scope);
    const firstFocusable = preferred ?? this.getFirstFocusable(scope);
    const target = firstFocusable || scope;

    if (!firstFocusable && target === scope) {
      target.setAttribute('tabindex', '-1');
    }

    // Move focus without auto-scrolling, then set scrollbar to very top
    try {
      target.focus({ preventScroll: true });
    } catch {
      try { target.focus(); } catch { /* noop */ }
    }

    // Always scroll window to the top of the page
    try {
      this.document.defaultView?.scrollTo({ top: 0, behavior: 'auto' });
    } catch {
      try { this.document.defaultView?.scrollTo(0, 0); } catch { /* noop */ }
    }
  }

  private getPreferredSkipTarget(root: HTMLElement): HTMLElement | null {
    const markers = [
      '[data-skip-target]',
      '[data-initial-focus]',
      '[nrInitialFocus]',
      '[autofocus]'
    ].join(',');
    const preferred = root.querySelector(markers) as HTMLElement | null;
    return preferred && this.isCandidate(preferred) ? preferred : null;
  }

  private getFirstFocusable(root: HTMLElement): HTMLElement | null {
    const selectors = [
      'a[href]','button:not([disabled])','input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])','textarea:not([disabled])','[tabindex]:not([tabindex="-1"])',
      '[role="button"]','[contenteditable="true"]'
    ].join(',');

    const candidates = Array.from(root.querySelectorAll<HTMLElement>(selectors))
      .filter((el) => this.isCandidate(el))
      .filter((el) => !this.isInExcludedRegion(el));

    return candidates[0] ?? this.getFirstHeading(root);
  }

  private getFirstHeading(root: HTMLElement): HTMLElement | null {
    const heading = root.querySelector('h1, h2, h3, [role="heading"]') as HTMLElement | null;
    if (heading && this.isVisible(heading)) {
      heading.setAttribute('tabindex', '-1');
      return heading;
    }
    return null;
  }

  private isCandidate(el: HTMLElement): boolean {
    return this.isVisible(el) && !this.isAriaHidden(el);
  }

  private isVisible(el: HTMLElement): boolean {
    const style = this.document.defaultView?.getComputedStyle(el);
    if (!style) return false;
    if (style.visibility === 'hidden' || style.display === 'none') return false;
    if (el.offsetParent === null && style.position !== 'fixed') return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  private isAriaHidden(el: HTMLElement): boolean {
    if (el.getAttribute('aria-hidden') === 'true') return true;
    const hiddenAncestor = el.closest('[aria-hidden="true"]');
    return !!hiddenAncestor;
  }

  private isInExcludedRegion(el: HTMLElement): boolean {
    const excluded = el.closest([
      'nav',
      '[role="navigation"]',
      'aside',
      '[role="complementary"]',
      'header',
      '[role="banner"]',
      '.sidenav',
      '.side-nav',
      '[class*="sidenav"]',
      '.sub-header',
      '.subheader',
      '[class*="sub-header"]',
      '[class*="subheader"]',
    ].join(','));
    return !!excluded;
  }
}
